import {addDays,eachDayOfInterval, format, subMinutes} from 'date-fns'
import {Dispatch, AnyAction} from 'redux'
import {
    BookingLocation,
    BookingBookingPostBody,
    BookingFeature,
    BookingFloorPlan,
    InitialBookingFeature,
    BookingBookingPayload,
    
} from '../../../services/booking/types'
import {BookingAvailabilityItem} from '../types'

import {BookingNewBooking} from '../Popover/types'
import {bookingService} from '../../../services/booking/bookingService'
import {formatDatTimeWithTimeZoneStr, formatDateWithTimeZone, formatTime} from '../../../utils/date-utils'
import {showSuccessMessage} from '../../../redux/reducers/snackbarReducer'
import {BaseResponse} from '../../../types/base-response'
import {dateToNumber, getDateRangeFromDateAndTimes,getJson} from '../utils/utils'
import {getJsonFromPath} from '../../../utils/app-utils'
import {BookingAvailabilityColors, BookingDateRange, BookingAvailability, ManagerBookingOptions} from '../consts'
import {BookingWeekdays} from './types'

import {
    getDashboardBookings,
    handleServerError,onBookingGridViewDataLoaded,
    refreshForwardBookingsCount,
    refreshMostUsedDesk,
} from '../bookingLogic'

import {addNewSearchResults} from '../../../redux/reducers/deskBookingSearchReducer'
import {setSubmissionsInProgress} from '../../../redux/reducers/deskBookingStatusesReducer'
import { BookingFeatureRenderOrder } from './enums'

export const getBookingMethod = (option: ManagerBookingOptions) => {
    switch (option) {
        case ManagerBookingOptions.MYSELF:
            return 'createBooking'
        case ManagerBookingOptions.EMPLOYEE:
            return 'createBookingOnBehalfOfEmployee'
        case ManagerBookingOptions.VISITOR:
            return 'createBookingOnBehalfOfVisitor'
        default:
            return 'createBooking'
    }
}

export const handleSingleBooking = async (
    feature: BookingFeature,
    deskName: string,
    {selectedRange, date}: BookingNewBooking,
    locations: BookingLocation[],
    floorplans: BookingFloorPlan[],
    employeeId: number,
    bookingFor: ManagerBookingOptions | undefined,
    userName: string,
    floorplanViewingDate: Date,
    dispatch: Dispatch<AnyAction>,
    onError: (err: string) => void,
    submissionsInProgress: number[]
) => {
    const floorPlanName = floorplans.find(f => f.id === feature.floorPlanId)?.name
    const newBooking: Partial<BookingBookingPostBody> = {
        createdByDate: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        createdByEmployeeId: employeeId,
        employeeName: userName || '',
        employeeId,
        featureId: feature.id,
        deskDetails: {
            deskName,
            floorPlanName: floorPlanName || '',
            location: locations.find(f => f.id === feature.locationId)?.name || '',
        },
        floorPlanId: feature.floorPlanId,
        fromDate: format(date, 'yyyy-MM-dd'),
        fromTime: format(selectedRange.from, 'HH:mm:ss'),
        toDate: format((selectedRange.to <= selectedRange.from ? addDays(date, 1) : date), 'yyyy-MM-dd'),
        toTime: format(selectedRange.to, 'HH:mm:ss'),
        status: 'Accepted',
    }

    return bookingService[getBookingMethod(bookingFor || ManagerBookingOptions.MYSELF)](newBooking)
        .then(result => {
            setTimeout(() => {
                dispatch(setSubmissionsInProgress(submissionsInProgress.filter(f => f !== feature.id)))
            }, 1000)
            bookingService
                .queryRecent({
                    fromDateTime: formatDatTimeWithTimeZoneStr(subMinutes(new Date(), 1)),
                    date: format(floorplanViewingDate, 'yyyy-MM-dd'),
                    floorPlanID: feature.floorPlanId,
                    from: formatTime(selectedRange.from),
                    to: formatTime(selectedRange.to),
        })
        .then(recentResult => {
          dispatch(addNewSearchResults(recentResult.bookings.filter(f => f.statusId === 2)))
        })
        .catch(err => {
          const response: BaseResponse = handleServerError(err.response.data)
          response.errors.forEach(error => {
            onError(error.name)
          })
                })
            
            dispatch(
                showSuccessMessage(
                    `You have successfully booked desk ${deskName} ${
                        floorPlanName ? `in ${floorPlanName}` : ''
                    }`
                )
            )
            refreshForwardBookingsCount()
            refreshMostUsedDesk()

            const singleDayBookingQuery = true
            getDashboardBookings(
                employeeId,
                floorplanViewingDate,
                dispatch,
                onBookingGridViewDataLoaded,
                singleDayBookingQuery,
                null,
                new Date(selectedRange.to),
                true,
                false
            )
        })
        .catch(err => {
            dispatch(setSubmissionsInProgress(submissionsInProgress.filter(f => f !== feature.id)))
            const response: BaseResponse = err.response.data
            response.errors.forEach(error => {
                onError(error.name)
            })
        })
}

export const getColor = (feature: BookingFeature) => {
    let fillColor = feature.fill
    if (fillColor.indexOf('zone') >= 0) {
        if (!feature?.zone?.additionalInfo) {
            fillColor = '#00000096'
        } else {
            const zoneAdditionalInfo = getJson(feature.zone.additionalInfo)
            fillColor = getJsonFromPath(zoneAdditionalInfo, fillColor.replace('zone.', ''))
        }
    }
    return fillColor
}

export const initialPopoverProps = () => {
    const date = new Date()
    return {
        mobileLayout: undefined,
        svgElement: undefined,
        open: false,
        x: 0,
        y: 0,
        featureDetail: InitialBookingFeature,
        isOwnBooking: false,
        isBlockBooking: false,
        isManager: false,
        floorplan: '',
        availability: BookingAvailability.AVAILABLE,
        availabilityColor: BookingAvailabilityColors.AVAILABLE,
        dateTimeFrom: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0),
        dateTimeTo: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 0),
    }
}

export const getDatesFromRange = (
    bookingRange: [Date, Date]
): { date: Date; dateNumber: number }[] =>
    eachDayOfInterval({
        start: formatDateWithTimeZone(bookingRange[BookingDateRange.FROM]),
        end: formatDateWithTimeZone(bookingRange[BookingDateRange.TO]),
    }).map(m => ({
        date: formatDateWithTimeZone(m),
        dateNumber: dateToNumber(formatDateWithTimeZone(m)),
    }))

export const getDatesFromBookings = (
    bookings: BookingBookingPayload[],
    weekdaysSelected?: BookingWeekdays[]
): BookingBookingPayload[] =>
    bookings
        .filter(
            f =>
                f.statusId === 2 &&
                (!weekdaysSelected ||
                    weekdaysSelected.some(s => s.dayIdx === new Date(f.fromDate).getDay()))
        )
        /**
 * Calculates the inclusive date range duration in seconds.
 * @param from The start date of the range.
 * @param to The end date of the range.
 * @returns The duration in seconds.
 */
const getDateRangeDurationInSeconds = (from: Date, to: Date): number => {
  const start = new Date(from)
  const end = new Date(to)
  start.setMilliseconds(0)
  end.setMilliseconds(0)
  const msDiff = end.getTime() - start.getTime() + 1000
  return Math.floor(msDiff / 1000)
}

/**
 * Calculates the total duration (in seconds) of the bookings within the period.
 * @param periodStart The start of the period.
           * @param periodEnd The end of the period.
 * @param bookings The bookings to calculate the duration for.
 * @returns The total duration (in seconds) of bookings within the given period.
 */
export const getBookedDurationWithinPeriod = (periodStart: Date, periodEnd: Date, bookings: BookingBookingPayload[]) => {
  let totalDuration = 0
  bookings.forEach(booking => {
    const { start: bookingStart, end: bookingEnd } = getDateRangeFromDateAndTimes(booking.fromDate, booking.fromTime, booking.toTime)
    const overlapStart = bookingStart >= periodStart ? bookingStart : periodStart
    const overlapEnd = bookingEnd <= periodEnd ? bookingEnd : periodEnd

            if (overlapEnd > overlapStart) {
                totalDuration += getDateRangeDurationInSeconds(overlapStart, overlapEnd)
                }
                })
                return totalDuration
        }

/**
 * Returns the desk availability in a given period.
 * @param startDate The start date of the period.
 * @param endDate The end date of the period.
 * @param bookings The bookings for a given desk.
 * @returns The desk availability in a given period.
 */
export const getBookingAvailabilityInPeriod = (startDate: Date, endDate: Date, bookings: BookingBookingPayload[]) => {
  const periodDuration = getDateRangeDurationInSeconds(startDate, endDate)
  const bookedDuration = getBookedDurationWithinPeriod(startDate, endDate, bookings)

  if (bookedDuration === 0) {
    return BookingAvailability.AVAILABLE
  }

  if (bookedDuration === periodDuration) {
    return BookingAvailability.NO_AVAILABILITY
  }

  return BookingAvailability.PARTIAL_AVAILABILITY
}

export const getPortAvailability = (
  datesFromRange: Date[],
  datesFromBookings: BookingBookingPayload[],
  timeRange: {
    start: string,
    end: string
  }
): BookingAvailabilityItem => {
  const firstDayRange = getDateRangeFromDateAndTimes(datesFromRange[0], timeRange.start, timeRange.end)
  const startDate = firstDayRange.start
  const endDate = firstDayRange.end
  const timeRangeDuration = getDateRangeDurationInSeconds(startDate, endDate)
  const bookedDurations = datesFromRange.map(date => {
    const { start, end } = getDateRangeFromDateAndTimes(date, timeRange.start, timeRange.end)
    return getBookedDurationWithinPeriod(start, end, datesFromBookings)
  })
  if (bookedDurations.every(duration => duration === 0)) {
    return BookingAvailability.AVAILABLE
  }

  if (bookedDurations.every(duration => duration === timeRangeDuration)) {
    return BookingAvailability.NO_AVAILABILITY
  }

  return BookingAvailability.PARTIAL_AVAILABILITY
}

export const sortFeatures = (features: BookingFeature[]): BookingFeature[] => 
    features.sort((a, b) => {
    const typeIndexA = BookingFeatureRenderOrder.indexOf(a.typeId);
    const typeIndexB = BookingFeatureRenderOrder.indexOf(b.typeId);
    
    const orderA = typeIndexA === -1 ? 999 : typeIndexA;
    const orderB = typeIndexB === -1 ? 999 : typeIndexB;
    
    return orderA - orderB;
});

const BOOKABLE_ZONE_FLOORPLANS = [
    {
        "id": 24,
        "categoryId": 1,
        "categoryName": "Desks",
        "locationId": 2,
        "locationName": "Manchester",
        "name": "MN3 - Floor 2",
        "image": ""
    },
    {
        "id": 22,
        "categoryId": 1,
        "categoryName": "Desks",
        "locationId": 2,
        "locationName": "Manchester",
        "name": "MN3 - Floor 3",
        "image": ""
    },
    {
        "id": 316,
        "categoryId": 1,
        "categoryName": "Desks",
        "locationId": 2,
        "locationName": "Manchester",
        "name": "MN3 - Floor 7 v2",
        "image": ""
    },
    {
        "id": 318,
        "categoryId": 1,
        "categoryName": "Desks",
        "locationId": 2,
        "locationName": "Manchester",
        "name": "MN3 - Floor 2 v2",
        "image": ""
    },
    {
        "id": 320,
        "categoryId": 1,
        "categoryName": "Desks",
        "locationId": 2,
        "locationName": "Manchester",
        "name": "MN3 - Floor 3 v2",
        "image": ""
    },
    {
        "id": 321,
        "categoryId": 1,
        "categoryName": "Desks",
        "locationId": 2,
        "locationName": "Manchester",
        "name": "MN3 - Floor 4 v2",
        "image": ""
    },
    {
        "id": 322,
        "categoryId": 1,
        "categoryName": "Desks",
        "locationId": 2,
        "locationName": "Manchester",
        "name": "MN3 - Floor 5 v2",
        "image": ""
    },
    {
        "id": 323,
        "categoryId": 1,
        "categoryName": "Desks",
        "locationId": 2,
        "locationName": "Manchester",
        "name": "MN3 - Floor 8 v2",
        "image": ""
    },
    {
        "id": 324,
        "categoryId": 1,
        "categoryName": "Desks",
        "locationId": 2,
        "locationName": "Manchester",
        "name": "MN3 - Floor 9 v2",
        "image": ""
    },
    {
        "id": 325,
        "categoryId": 1,
        "categoryName": "Desks",
        "locationId": 2,
        "locationName": "Manchester",
        "name": "MN3 - Floor 10 v2",
        "image": ""
    }
]
const OPEN_FLOORPLANS_LOCATIONS = [
    {
        "id": 2,
        "name": "Manchester"
    },
    {
        "id": 10,
        "name": "Manchester"
    },
    {
        "id": 18,
        "name": "Manchester"
    }
]

export const checkIsOpenFloorplanArea = (locationId: number, floorplanId: number) =>
    OPEN_FLOORPLANS_LOCATIONS.filter(location => location.id === locationId)[0]
    && !BOOKABLE_ZONE_FLOORPLANS.filter(floorplan => floorplan.id === floorplanId)[0]
